import flatpickr from 'flatpickr';
import type { PointLabelObject, TooltipFormatterContextObject } from 'highcharts';
import Highcharts, { numberFormat } from 'highcharts';
import { DatepickerDirective } from 'src/app/common/directives/datepicker.directive';

function formatNumber(value: number, thousandSep: boolean = false): string {
  return numberFormat(value, 2, ',', thousandSep ? '.' : '');
}

function getMatchingSeries(point: PointLabelObject): Highcharts.Series | null {
  const colorIndex: number = point.colorIndex;
  for (const series of point.series.chart.series) {
    if (series['colorIndex'] === colorIndex) {
      return series;
    }
  }
  return null;
}

function formatColoredDot(point: PointLabelObject, tooltip: unknown): string {
  return `<span class="chart-tooltip-point chart-tooltip-color-${point.colorIndex}">${tooltip}</span>`;
}

function formatSeriesPointNameAndValue(point: PointLabelObject, value: number, displayColoredDot: boolean): string {
  const formattedValue: string = formatNumber(value);
  const series: Highcharts.Series | null = getMatchingSeries(point);
  const seriesName: string = series ? series.name : 'Unbekannt';

  const tooltip: string = `${seriesName}: ${formattedValue}`;

  if (displayColoredDot) {
    return formatColoredDot(point, tooltip);
  }

  return tooltip;
}

function formatDate(date: number | string | Date): string {
  if (typeof date === 'string') {
    date = Number.parseInt(date, 10);
  }

  if (typeof date === 'number') {
    date = new Date(date);
  }

  return flatpickr.formatDate(date, DatepickerDirective.DISPLAY_FORMAT);
}

export function valueDevelopmentTooltipFormatter(this: TooltipFormatterContextObject): string[] {
  const xAxisTooltip: string = formatDate(this.x);
  const tooltips: Array<string> = [xAxisTooltip];

  for (const point of this.points) {
    const value: number = point.point['change'];
    const seriesTooltip: string = formatSeriesPointNameAndValue(point, value, true);
    tooltips.push(seriesTooltip);
  }

  return tooltips;
}

export function barTooltipFormatter(this: TooltipFormatterContextObject): string {
  const value: number = this.y;
  const axisStr: string | number = this.x;
  const valueStr: string = formatSeriesPointNameAndValue(this, value, true);
  return `${axisStr}<br/>${valueStr}`;
}

export function doughnutTooltipFormatter(this: TooltipFormatterContextObject): string {
  const value: number = this.y;
  const axisStr: string | number = this.point.name;
  const valueStr: string = formatColoredDot(this, formatNumber(value, true));
  return `${axisStr}<br/>${valueStr} EUR`;
}

export function doughnutCenterLabelFormatter(sum: number | string, headline?: string) {
  let headlineStr: string = '';

  if (headline) {
    const fontSzie: string = 'font-size: var(--pie-chart-center-label-headline-font-size);';
    const fill: string = 'fill: rgb(var(--sd-color-neutral-700,104 104 104));';
    headlineStr = `<tspan style="${fontSzie}${fill}}">${headline}</tspan><br>`;
  }

  const valueStr: string = typeof sum === 'number' ? formatNumber(sum, true) + ' EUR' : sum;

  return `${headlineStr}<strong>${valueStr}<strong>`;
}

export function countryNoBmkFormatter(this: Highcharts.TooltipFormatterContextObject) {
  const place: string = this.point.series.userOptions.custom['labels'][this.point.index];
  const value: string = formatNumber(this.point.value);
  return `${place}: ${value}`;
}

export function countryBmkFormatter(this: Highcharts.TooltipFormatterContextObject) {
  const place: string = this.point.series.userOptions.custom['labels'][this.point.index];
  const value: string = formatNumber(this.point.value);
  const bmk: string = formatNumber(this.point.series.userOptions.custom['bmk'][this.point.index]);
  return `${place}: ${value}<br/>BMK: ${bmk}`;
}
